import { nanoid } from 'nanoid'
import Cookies from 'js-cookie'
import { getTimestamp, sendGTMEvent, isDataLayerAvailable } from 'shared/lib'
import { ZIndex } from 'shared/constants/zIndex'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { getParserToken } from 'entities/Auth/lib/setToken'

const id = nanoid()

const getAllCookies = () => {
  return document.cookie
    .split('; ')
    .map((item) => item.split('='))
    .reduce((c: { [key: string]: string }, n) => {
      const key = n[0]
      const value = n[1]

      if (key) {
        c[key] = value
      }

      return c
    }, {})
}

const payload = (entities: IEntitiesStore) => {
  const user = entities.usersStore.user
  const number = entities.numbersStore.organizationNumber
  const config = entities.configStore.config
  const plan = entities.subscriptionStore.plan
  const z_index = ZIndex.BASE_30
  const anonymous_id = (() => {
    const exist_id = localStorage.getItem('intercom-anonymous_id')

    if (!exist_id) {
      localStorage.setItem('intercom-anonymous_id', id)
    }

    return localStorage.getItem('intercom-anonymous_id')
  })()

  if (!user || !number || !config || !plan || !entities.organizationStore.id) {
    return {
      anonymous_id: anonymous_id,
      z_index: z_index,
    }
  }

  const company = {
    id: entities.organizationStore.id,
    name: entities.organizationStore.name,
    createdAt: entities.organizationStore.created_at,
    ownerFirstName: user.first_name,
    ownerLastName: user.last_name,
    ownerName: user.full_name,
    phone: number?.formatted_number,
    plan: plan.id,
    planStatus: entities.subscriptionStore.status,
  }

  return {
    email: user.email,
    id: String(user.id),
    user_id: String(user.id),
    created_at: getTimestamp(user.createdAt),
    name: user.name,
    phone: number.formatted_number,
    avatar: user.avatarInfo.image,
    user_hash: config.intercom,
    company,
    z_index: z_index,
  }
}

type IinitIntercomProps = {
  fromAuthorizedApp?: boolean
  entities: IEntitiesStore
}

export const initIntercom = async ({ fromAuthorizedApp, entities }: IinitIntercomProps) => {
  if (!isDataLayerAvailable()) return

  if (fromAuthorizedApp) {
    await Promise.all([
      entities.organizationStore.initPromise,
      entities.configStore.initPromise,
      entities.numbersStore.initFetchOrganizationNumberPromise,
      entities.subscriptionStore.initPromise,
    ])
  }

  const data = getParserToken()

  if (!data || !data?.hipaa) {
    sendGTMEvent({
      eventName: 'intercom_init',
      eventParams: { intercom_payload: payload(entities) },
    })
  }
}

// https://developers.intercom.com/installing-intercom/web/methods
export const updateIntercom = (entities: IEntitiesStore) => {
  if (!window.Intercom) return

  const data = getParserToken()

  if (!data || !data?.hipaa) {
    window.Intercom('update', payload(entities))
  }
}

export const openIntercom = () => {
  if (!window.Intercom) return

  const data = getParserToken()

  if (!data || !data?.hipaa) {
    window.Intercom('show')
  }
}

export const openIntercomMessages = () => {
  if (!window.Intercom) return

  const data = getParserToken()

  if (!data || !data?.hipaa) {
    window.Intercom('showMessages')
  }
}

export const openIntercomNewMessage = (prePopulatedMessage?: string) => {
  const data = getParserToken()

  if (!data || !data?.hipaa) {
    window.Intercom?.('showNewMessage', prePopulatedMessage ?? '')
  }
}

export const clearSessionIntercom = () => {
  if (!window.Intercom) {
    for (const key in getAllCookies()) {
      if (/^intercom-session-.+/.test(key)) {
        Cookies.remove(key, { domain: '.salesmessage.com' })
      }
    }

    return
  }

  window.Intercom('shutdown')
}

export const setHideIntercom = (value: boolean) => {
  if (!window.Intercom) return

  const data = getParserToken()

  if (!data || !data?.hipaa) {
    window.Intercom('update', {
      hide_default_launcher: value,
    })
  }
}
